import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Box,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  Grid,
  Chip,
  Avatar
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import WarningIcon from '@mui/icons-material/Warning';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';

// Mock usage data for a customer over time
const generateUsageData = (customer) => {
  return Array.from({ length: 12 }, (_, i) => {
    const baseValue = customer.value / 20;
    const multiplier = customer.usage === 'Düşen' 
      ? 1.2 - (i/10) 
      : customer.usage === 'Düşük' 
        ? 0.7 + (Math.random() * 0.2) 
        : 0.9 + (Math.random() * 0.3);
    
    return {
      month: ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'][i],
      usage: Math.round(baseValue * multiplier),
      avgUsage: Math.round(baseValue),
    };
  });
};

// Risk factors for the customer
const generateRiskFactors = (customer) => {
  const factors = [
    { factor: 'Kullanım Düşüşü', value: customer.usage === 'Düşen' ? 82 : 35 },
    { factor: 'Sözleşme Yakında Bitiyor', value: customer.tenure < 10 ? 75 : 15 },
    { factor: 'Destek Çağrıları', value: Math.random() > 0.5 ? 65 : 25 },
    { factor: 'Fiyat Duyarlılığı', value: customer.segment === 'Bireysel' ? 70 : 40 },
    { factor: 'Rakip Kullanımı', value: Math.random() > 0.7 ? 85 : 20 }
  ];
  
  return factors.sort((a, b) => b.value - a.value);
};

const CustomerDetailDialog = ({ open, onClose, customer }) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleClose = () => {
    onClose();
  };

  if (!customer) return null;

  const usageData = generateUsageData(customer);
  const riskFactors = generateRiskFactors(customer);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div">
            Müşteri Risk Profili
          </Typography>
          <Chip
            label={`Risk Skoru: ${customer.risk}%`}
            color={customer.risk >= 80 ? "error" : customer.risk >= 60 ? "warning" : "success"}
            variant="outlined"
            size="small"
            icon={customer.risk >= 80 ? <WarningIcon /> : undefined}
            sx={{ fontWeight: 'bold' }}
          />
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pb: 1 }}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="div">
                    {customer.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {customer.segment} Müşteri • {customer.tenure} Ay
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Müşteri Değeri:
                </Typography>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(customer.value)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Kullanım Seviyesi:
                </Typography>
                <Chip 
                  size="small" 
                  label={customer.usage}
                  color={customer.usage === 'Düşen' ? 'error' : customer.usage === 'Düşük' ? 'warning' : 'success'}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Risk Faktörleri" icon={<WarningIcon />} iconPosition="start" />
            <Tab label="Kullanım Analizi" icon={<TimelineIcon />} iconPosition="start" />
            <Tab label="Önerilen Aksiyonlar" icon={<BarChartIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <Box sx={{ pt: 2 }}>
          {/* Risk Faktörleri */}
          {activeTab === 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Çok Yüksek Riskli Faktörler
              </Typography>

              <List dense disablePadding>
                {riskFactors.map((factor, index) => (
                  <ListItem 
                    key={index} 
                    disablePadding 
                    sx={{ py: 0.5, borderBottom: index < riskFactors.length - 1 ? '1px solid #eee' : 'none' }}
                  >
                    <ListItemText 
                      primary={factor.factor} 
                      secondary={
                        <Box sx={{ 
                          width: '100%', 
                          height: 4, 
                          bgcolor: 'grey.200', 
                          borderRadius: 2, 
                          mt: 0.5 
                        }}>
                          <Box 
                            sx={{ 
                              width: `${factor.value}%`, 
                              height: '100%', 
                              bgcolor: factor.value > 70 ? 'error.main' : 
                                      factor.value > 40 ? 'warning.main' : 'success.main',
                              borderRadius: 2
                            }} 
                          />
                        </Box>
                      }
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {factor.value}%
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {/* Kullanım Analizi */}
          {activeTab === 1 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Son 12 Ay Kullanım Trendi
              </Typography>
              
              <Box sx={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={usageData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="usage" 
                      name="Müşteri Kullanımı" 
                      stroke="#8884d8" 
                      activeDot={{ r: 8 }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="avgUsage" 
                      name="Ortalama Kullanım" 
                      stroke="#82ca9d" 
                      strokeDasharray="5 5" 
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          )}

          {/* Önerilen Aksiyonlar */}
          {activeTab === 2 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Müşteri Kayıp Önleme Stratejileri
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Fiyat İndirimi Teklifi" 
                    secondary="Son fatura tutarından %15 indirim önerisi" 
                  />
                  <Chip size="small" label="Yüksek Etki" color="success" variant="outlined" />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="VIP Müşteri Desteği" 
                    secondary="Özel destek yöneticisi atanması" 
                  />
                  <Chip size="small" label="Orta Etki" color="primary" variant="outlined" />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Ürün Eğitimi" 
                    secondary="Kullanım potansiyelini artırmak için eğitim webinarı" 
                  />
                  <Chip size="small" label="Orta Etki" color="primary" variant="outlined" />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Ek Özellik" 
                    secondary="3 ay boyunca ücretsiz premium özellik erişimi" 
                  />
                  <Chip size="small" label="Yüksek Etki" color="success" variant="outlined" />
                </ListItem>
              </List>
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Kapat</Button>
        <Button variant="contained" color="primary" onClick={handleClose}>
          Aksiyon Planı Oluştur
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerDetailDialog;
